/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslate } from '@im/react-localization';
import { blue, green, orange } from '@mui/material/colors';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '@im/ui-lib/Pagination';

import { DataGrid, NoRowsOverlay } from 'src/components/DataGrid';
import useSnackBar from 'src/hooks/useSnackBar';

import {
  BCNListResponse,
  useDeleteBCN,
  useGetBCNList,
} from '../hooks/multi-bcn';

const DEFAULT_PAGE_SETUP = {
  page: 1,
  pageSize: 5,
};

type PageSetup = {
  page: number;
  pageSize: number;
};

type CommonProps = Readonly<{
  AddBcnPageComponent: React.ComponentType<{
    BcnCameOnDeal: boolean;
    bcn_linking_level: string;
    customerSetId: string;
    dealId: string;
    handleClose: () => void;
    resellerName: string;
  }>;
  BcnCameOnDeal: boolean;
  bcn_linking_level: string;
  branchCodes?: string[] | null;
  customerSetId: string;
  dealId: string;
  discountType?: string;
  enablePagination?: boolean;
  generateQuote?: (selectedBcn: string[], disabled: boolean) => ReactNode;
  handleClose: () => void;
  isResolvedOnException: boolean;
  resellerAuth: string;
  resellerName: string;
  showBCNAction: boolean;
  showResolutionType?: boolean;
  vendor_program_id?: string | number;
}>;

export const getResolutionColor = (
  row: GridRenderCellParams['row']
): string[] =>
  [
    row.isResolvedOnException && orange[500],
    row.bcnCameOnDeal && green[500],
    row.isDefaultBcn && blue[500],
  ].filter(Boolean) as string[];

export default function BCNListDialogCommon(props: CommonProps) {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const [searchParams] = useSearchParams();
  const showResellerManagementDetails = Boolean(props.showResolutionType);

  const importreferenceid = searchParams.get('importreferenceid');
  const normalizedImportReferenceId =
    importreferenceid &&
    !['null', 'undefined'].includes(importreferenceid.trim().toLowerCase())
      ? importreferenceid
      : 0;

  const [isAddingBcnPage, setIsAddingBcnPage] = useState<boolean>(false);
  const [selectedBcn, setSelectedBcn] = useState<string[]>([]);
  const [bcn, setBcn] = useState<string>('');

  const [pageSetup, setPageSetup] = useState<PageSetup>(DEFAULT_PAGE_SETUP);

  const { data, dataUpdatedAt, isLoading, refetch } = useGetBCNList(
    {
      bcn_linking_level: props.bcn_linking_level,
      customerset_Id: props.customerSetId,
      deal_Id: props.dealId,
      importreferenceid: normalizedImportReferenceId,
      reseller_authorization: props.resellerAuth,
      ...(props.vendor_program_id !== undefined
        ? { vendor_program_id: props.vendor_program_id }
        : {}),
    },
    props.discountType !== 'BranchDiscount'
  );
  const deleteBCN = useDeleteBCN();

  useEffect(() => {
    if (data?.length === 1 && props.discountType !== 'BranchDiscount') {
      setSelectedBcn([data[0].bcn]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  useEffect(() => {
    refetch();
  }, [deleteBCN.isSuccess, refetch, isAddingBcnPage]);

  const validateBcn = (value: string) => {
    if (!value || value.length === 0) {
      return true;
    }

    const regex = /^[A-Z0-9a-z]{8}$/;

    const result = value.split(',').map((item) => item.trim());

    for (const item of result) {
      if (
        !regex.test(item) ||
        (props.branchCodes && !props.branchCodes.includes(item.slice(0, 2)))
      ) {
        return false;
      }
    }

    return true;
  };

  const handleBcnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setBcn(value);

    if (validateBcn(value)) {
      setSelectedBcn(value.split(',').map((item) => item.trim()));
    } else {
      setSelectedBcn([]);
    }
  };

  const columnHeaders = [
    {
      field: 'resellerName',
      headerName: translate('VendorProgram.ResellerName'),
    },
    {
      field: 'bcn',
      headerName: translate('VendorProgram.BCN'),
    },
    ...(showResellerManagementDetails
      ? [
          {
            field: 'resolutionType',
            headerName: translate('VendorProgram.ResolutionType'),
          },
        ]
      : []),
  ];
  const columns: GridColDef[] = columnHeaders.map((column, index) => ({
    field: column.field,
    flex: 1,
    headerName: column.headerName,
    renderCell: (item) => {
      if (index !== 0) {
        return item.value;
      }
      if (!showResellerManagementDetails) {
        return item.value;
      }
      const colors = getResolutionColor(item.row);

      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            width: '100%',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              justifyContent: 'center',
              mr: 1,
              width: 10,
            }}
          >
            {colors.map((color) => (
              <Box
                key={color}
                sx={{
                  backgroundColor: color,
                  borderRadius: '50%',
                  flexShrink: 0,
                  height: 10,
                  width: 10,
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
            }}
          >
            {item.value}
          </Box>
        </Box>
      );
    },
  }));

  if (props.showBCNAction) {
    columns.push({
      align: 'center',
      field: 'delete',
      headerAlign: 'center',
      headerName: '',
      renderCell: (item: GridRenderCellParams<BCNListResponse>) => (
        <Tooltip title={translate('Common.Delete')}>
          <IconButton
            aria-label="delete"
            data-testid={`delete-bcn-${item.row.bcn}`}
            disabled={item.row.isResolvedOnException}
            onClick={() => {
              deleteBCN
                .mutateAsync({
                  BCN: item.row.bcn,
                  BcnCameOnDeal: item.row.bcnCameOnDeal ?? false,
                  bcn_linking_level: props.bcn_linking_level,
                  customerset_Id: props.customerSetId,
                  deal_Id: props.dealId,
                  importreferenceid: importreferenceid ?? '',
                  reseller_authorization: props.resellerAuth,
                })
                .then(() => {
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.Deleted'),
                    severity: 'success',
                  });
                })
                .catch(() => {
                  showSnackBar({
                    autoHide: 2000,
                    message: translate('Common.Failed'),
                    severity: 'error',
                  });
                });
            }}
            size="small"
            sx={{
              '& .MuiSvgIcon-root': {
                color: item.row.sourceSystem === 'gde' ? blue[900] : undefined,
              },
              '&.Mui-disabled': {
                '& .MuiSvgIcon-root': {
                  color: (theme: any) => theme.palette.action.disabled,
                },
              },
              backgroundColor: 'transparent',
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      ),
      width: 80,
    });
  }

  const rows = data ?? [];
  const [searchValue, setSearchValue] = useState<string>('');

  const handleOnSearch = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { value } = event.target;
    setPageSetup({ ...pageSetup, page: 1 });
    setSearchValue(value);
  };
  const search = searchValue.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    if (!search) {
      return true;
    }
    return Object.values(row).some((value) => {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        return String(value).toLowerCase().includes(search);
      }
      return false;
    });
  });

  useEffect(() => {
    if (!props.enablePagination) {
      return;
    }
    const pageCount = Math.ceil(filteredRows.length / pageSetup.pageSize) || 1;

    if (pageSetup.page > pageCount) {
      setPageSetup((currentPageSetup) => ({
        ...currentPageSetup,
        page: pageCount,
      }));
    }
  }, [
    pageSetup.page,
    pageSetup.pageSize,
    filteredRows.length,
    props.enablePagination,
  ]);

  const paginatedRows = props.enablePagination
    ? filteredRows.slice(
        (pageSetup.page - 1) * pageSetup.pageSize,
        pageSetup.page * pageSetup.pageSize
      )
    : filteredRows;

  const requestListPagination = () => (
    <Pagination
      data-testid="gds-vp-bcn-list-table-pagination"
      onPageChange={(page) => {
        setPageSetup((currentPageSetup) => ({
          ...currentPageSetup,
          page,
        }));
      }}
      onRowsPerPageChange={(pageSize) => {
        setPageSetup({
          page: 1,
          pageSize,
        });
      }}
      page={pageSetup.page}
      rowsPerPage={pageSetup.pageSize}
      rowsPerPageOptions={[5, 10, 25, 50]}
      totalCount={rows.length}
    />
  );

  const isBcnValid = validateBcn(bcn);

  const handleAddBcnPage = () => {
    setIsAddingBcnPage((currentValue) => !currentValue);
  };
  const resolutionLegend = [
    { color: orange[500], label: 'Autoquoted BCN' },
    { color: green[500], label: 'BCN on Deal' },
    { color: blue[500], label: 'Default BCN' },
  ];
  return (
    <Dialog
      data-testid="vp-bcnList-dialog"
      hidden={!!isAddingBcnPage}
      maxWidth="md"
      onClose={props.handleClose}
      open={true}
    >
      <DialogTitle data-testid="vp-bcnList-dialog-title" sx={{ pb: 1, pt: 2 }}>
        <Stack>
          {translate('VendorProgram.BcnListTitle')}

          {props.discountType === 'BranchDiscount' && (
            <Typography fontSize="14px">
              {translate('VendorProgram.EnterBcnDescription')}
            </Typography>
          )}
        </Stack>

        <IconButton onClick={props.handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          mb={1}
        >
          <Box
            sx={{
              fontWeight: showResellerManagementDetails ? 'bold' : 'normal',
            }}
          >
            {translate('VendorProgram.BCNLinkingLevel')}:{' '}
            {props.bcn_linking_level}{' '}
            {data?.[0]?.hasBCNOnBid === true &&
              `(${translate('VendorProgram.BCNOnDeal')})`}
          </Box>
          {props.showBCNAction && (
            <Stack flexDirection="row" justifyContent="flex-end">
              <Button
                data-testid="gds-vp-vpn-list-addpropd"
                onClick={handleAddBcnPage}
                startIcon={<AddIcon />}
                variant="newContained"
              >
                {translate('VendorProgram.AddBCN')}
              </Button>
            </Stack>
          )}
        </Stack>
        {showResellerManagementDetails && (
          <Box maxWidth={200} mb={1.5}>
            <Tooltip
              componentsProps={{
                popper: {
                  style: {
                    zIndex: 1500,
                  },
                },
              }}
              title={props.resellerName ?? ''}
            >
              <Typography
                style={{ color: '#0077d4' }}
                sx={{
                  color: '#0077d4',
                  fontSize: 14,
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {props.resellerName ?? ''}
              </Typography>
            </Tooltip>
          </Box>
        )}
        {isAddingBcnPage && (
          <props.AddBcnPageComponent
            BcnCameOnDeal={props.BcnCameOnDeal}
            bcn_linking_level={props.bcn_linking_level}
            customerSetId={String(props.customerSetId)}
            dealId={String(props.dealId)}
            handleClose={() => setIsAddingBcnPage(false)}
            resellerName={props.resellerName}
          />
        )}

        {props.discountType === 'BranchDiscount' ? (
          <Box mb={2} mt={6}>
            <TextField
              disabled={!props.branchCodes}
              error={!isBcnValid}
              fullWidth
              helperText={translate(
                isBcnValid
                  ? 'VendorProgram.EnterBcnHelperText'
                  : 'VendorProgram.EnterBcnErrorMessage'
              )}
              id="bcnTextField"
              label={translate('VendorProgram.EnterBcnLabel', {
                branchCodes: props.branchCodes?.join(', ') ?? '',
              })}
              onChange={handleBcnChange}
              placeholder={translate('VendorProgram.EnterBcnPlaceholder')}
              sx={{
                '.MuiInputLabel-root': {
                  color: '#333',
                  fontSize: '20px',
                  fontWeight: 600,
                },
              }}
              value={bcn}
            />
          </Box>
        ) : (
          <Box>
            {showResellerManagementDetails && (
              <Box pb={1}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    data-testid="gds-vp-aff-reselleler-bcn-search"
                    name="ResellerNameSearchField"
                    onChange={handleOnSearch}
                    placeholder={translate(
                      'VendorProgram.SearchResellerPlaceholder'
                    )}
                    sx={{ width: '40%' }}
                    value={searchValue}
                    variant="outlined"
                  />
                </Stack>
              </Box>
            )}
            <DataGrid
              autoHeight
              checkboxSelection={Boolean(props.generateQuote)}
              columnBuffer={columns.length}
              columns={columns}
              components={{
                NoRowsOverlay: NoRowsOverlay,
              }}
              data-testid="gds-vp-aff-end-user-dialog-data-grid"
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              getRowId={(row: GridRowModel) => `${row.bcn}`}
              hideFooter={props.enablePagination}
              hideFooterPagination={!props.enablePagination}
              keepNonExistentRowsSelected={props.enablePagination}
              loading={isLoading}
              onSelectionModelChange={(selectionModel) =>
                setSelectedBcn(selectionModel as string[])
              }
              rows={paginatedRows}
              selectionModel={selectedBcn}
              sx={{
                '.MuiDataGrid-cell': {
                  outline: 'transparent !important',
                  overflow: 'hidden',
                  padding: '0 16px',
                },
                '.MuiDataGrid-columnHeader--moving': {
                  backgroundColor: 'transparent',
                },
                '.MuiDataGrid-columnHeaderCheckbox': {
                  padding: 0,
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                },
                '.MuiDataGrid-footerContainer>div': {
                  flexWrap: 'wrap',
                },
                '.MuiDataGrid-footerContainer>div>div': {
                  flex: 1,
                },
                '.MuiDataGrid-row': {
                  minHeight: '52px !important',
                },
              }}
            />

            {props.enablePagination && (
              <Box mt={2}>{requestListPagination()}</Box>
            )}
            {showResellerManagementDetails && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'flex-end',
                  mb: 1,
                }}
              >
                {resolutionLegend.map(({ color, label }) => (
                  <Box
                    key={label}
                    sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color,
                        borderRadius: '50%',
                        height: 10,
                        width: 10,
                      }}
                    />
                    <Typography>{label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {props.generateQuote ? (
          props.generateQuote(selectedBcn, isLoading)
        ) : (
          <Button
            data-testid="vp-bcnList-dialog-okay-button"
            onClick={props.handleClose}
            variant="newContained"
          >
            {translate('Common.Okay')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
